"use client";

import { useRelayTokenPrice } from "@/hooks/useRelayTokenPrice";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { memo, useEffect, useMemo, useState } from "react";
import { rawToDecimal } from "../TriggerInputModal";
import BalancePresetButtons from "./BalancePresetButtons";
import TokenChainDropdown from "./TokenChainDropdown";
import { useTokenBalancePresets } from "./useTokenBalancePresets";

interface TokenChainAmountInputProps {
  /** Amount in wei (smallest units) */
  amount: string;
  onAmountChange: (amount: string) => void;
  tokenAddress: string;
  chainId: number;
  onTokenChange: (address: string, chainId: number, symbol: string, decimals: number) => void;
  label?: string;
  placeholder?: string;
  popoverModal?: boolean;
  workflowWalletAddress?: string;
  /** When true, hides native coins (ETH, etc.) from the token dropdown */
  excludeNative?: boolean;
  /** Called when insufficient balance state changes */
  onInsufficientBalance?: (insufficient: boolean) => void;
}

export default memo(function TokenChainAmountInput({
  amount,
  onAmountChange,
  tokenAddress,
  chainId,
  onTokenChange,
  label,
  placeholder = "0.00",
  popoverModal = false,
  workflowWalletAddress,
  excludeNative = false,
  onInsufficientBalance,
}: TokenChainAmountInputProps) {
  const [displayValue, setDisplayValue] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Token metadata for decimals + symbol
  const { data: selectedTokenResp } = useSimTokenInfo(tokenAddress, chainId, !!tokenAddress);
  const selectedToken = selectedTokenResp?.tokens?.[0];
  const decimals = selectedToken?.decimals ?? 18;
  const tokenSymbol = selectedToken?.symbol ?? "tokens";

  // Token price for USD estimate
  const { data: tokenPrice } = useRelayTokenPrice({
    tokenAddress,
    chainId,
    enabled: !!(tokenAddress && chainId),
  });

  // Wallet balance, presets, and insufficient balance check
  const { tokenBalanceWei, balanceDisplay, applyPreset, isInsufficientBalance, isNativeToken } = useTokenBalancePresets(
    {
      workflowWalletAddress,
      tokenAddress,
      chainId,
      decimals,
      tokenPrice,
      tokenSymbol,
      amount,
      onAmountChange,
      onIsUserTyping: setIsUserTyping,
    },
  );

  // Convert wei → display value (when not typing)
  useEffect(() => {
    if (isUserTyping) return;
    if (!amount) {
      setDisplayValue("");
      return;
    }
    setDisplayValue(rawToDecimal(amount, decimals));
  }, [amount, decimals, isUserTyping]);

  // Convert display value → wei on user input
  const handleAmountInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue && !/^[0-9]*\.?[0-9]*$/.test(newValue)) return;
    setDisplayValue(newValue);
    setIsUserTyping(true);

    if (!newValue || newValue === "0") {
      onAmountChange(newValue ? "0" : "");
      return;
    }
    try {
      const [wholePart, decimalPart = ""] = newValue.split(".");
      if (decimals === 0) {
        onAmountChange(BigInt(wholePart || 0).toString());
        return;
      }
      const wholeWei = BigInt(wholePart || 0) * BigInt(10) ** BigInt(decimals);
      let decimalWei = 0n;
      if (decimalPart) {
        const paddedDecimal = decimalPart.padEnd(decimals, "0").slice(0, decimals);
        decimalWei = BigInt(paddedDecimal);
      }
      onAmountChange((wholeWei + decimalWei).toString());
    } catch {
      // User is still typing (e.g. just a ".")
    }
  };

  const handleBlur = () => setIsUserTyping(false);

  // USD estimate from display value
  const usdEstimate = useMemo(() => {
    const num = parseFloat(displayValue);
    if (!tokenPrice || isNaN(num) || num <= 0) return null;
    return num * tokenPrice;
  }, [displayValue, tokenPrice]);

  useEffect(() => {
    onInsufficientBalance?.(isInsufficientBalance);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire when balance status changes, not on callback ref change
  }, [isInsufficientBalance]);

  return (
    <div>
      {label && <label className="field-label mb-1.5 block">{label}</label>}

      <TokenChainDropdown
        tokenAddress={tokenAddress}
        chainId={chainId}
        onTokenChange={onTokenChange}
        popoverModal={popoverModal}
        workflowWalletAddress={workflowWalletAddress}
        excludeNative={excludeNative}
      >
        {pill => (
          <div className="border-ds-border-default hover:border-ds-neutral-300 bg-ds-surface-card flex cursor-default items-center rounded-xl border transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/30 dark:hover:border-zinc-600">
            {/* Token pill — left, aligned with Buy */}
            <div className="ml-1.5">{pill}</div>
            {/* Amount input — right, right-aligned text (finance convention) */}
            <input
              type="text"
              inputMode="decimal"
              value={displayValue}
              onChange={handleAmountInput}
              onBlur={handleBlur}
              onClick={e => e.stopPropagation()}
              placeholder={placeholder}
              className="text-ds-text-primary dark:placeholder:text-ds-text-subtle min-w-0 flex-1 cursor-text bg-transparent px-4 py-3 text-right text-lg font-semibold outline-none placeholder:text-zinc-300"
            />
          </div>
        )}
      </TokenChainDropdown>

      {/* USD estimate + balance + presets — single row */}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 px-1">
        {usdEstimate !== null && (
          <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs">
            ≈ ${usdEstimate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
          </span>
        )}

        {workflowWalletAddress && tokenAddress && (
          <BalancePresetButtons
            balanceDisplay={balanceDisplay}
            applyPreset={applyPreset}
            isNativeToken={isNativeToken}
          />
        )}
      </div>

      {isInsufficientBalance && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
          Insufficient balance. You need more {tokenSymbol} to complete this transaction.
        </p>
      )}
    </div>
  );
});
